import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SetupWizardStep } from '../../leagueSetupWizard';
import {
  SCHEDULE_SEASON_ROLLOVER_COPY,
  SCHEDULE_SETUP_INTRO_COPY,
} from '../../setupScheduleCopy';
import { SCHEDULE_REPAIR_SETUP_LINK_COPY } from '../../scheduleRepairCopy';
import type { LscLeague, LscLeagueReadiness } from '../../types';
import CsvImportPanel from '../CsvImportPanel';
import LeagueSetupStepCard from '../LeagueSetupStepCard';
import NeonSportzUrlImportPanel from '../NeonSportzUrlImportPanel';
import ScheduleSourcePanel from '../ScheduleSourcePanel';
import ScheduleSyncStatusPanel from '../ScheduleSyncStatusPanel';
import { displaySeasonLabel } from '../../seasonDisplay';

const SCHEDULE_READINESS_KEYS = [
  'schedule_source_connected',
  'current_week_synced',
  'active_games',
  'games_visible',
] as const;

export interface ScheduleSetupStepPanelProps {
  leagueId: string;
  league: LscLeague;
  wizardStep: SetupWizardStep;
  readiness: LscLeagueReadiness;
  leagueArchived?: boolean;
  onSetupRefresh: () => void;
}

function scheduleAttentionItems(readiness: LscLeagueReadiness) {
  return (readiness.items ?? []).filter(
    (item) =>
      SCHEDULE_READINESS_KEYS.includes(item.key as (typeof SCHEDULE_READINESS_KEYS)[number]) &&
      item.status !== 'complete' &&
      item.status !== 'not_applicable'
  );
}

const ScheduleSetupStepPanel: React.FC<ScheduleSetupStepPanelProps> = ({
  leagueId,
  league,
  wizardStep,
  readiness,
  leagueArchived = false,
  onSetupRefresh,
}) => {
  const attentionItems = useMemo(() => scheduleAttentionItems(readiness), [readiness]);
  const staleOrBlocked = attentionItems.some(
    (item) =>
      item.key === 'current_week_synced' &&
      (item.status === 'blocked' || item.status === 'warning')
  );

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="schedule-setup-step-panel">
      <LeagueSetupStepCard step={wizardStep} />

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-300">
        {SCHEDULE_SETUP_INTRO_COPY}
      </div>

      <div
        className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 text-sm text-amber-100 flex gap-2"
        data-testid="schedule-season-rollover-notice"
      >
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
        <p>{SCHEDULE_SEASON_ROLLOVER_COPY}</p>
      </div>

      {staleOrBlocked ? (
        <div
          className="rounded-lg border border-amber-700/40 bg-amber-950/10 px-4 py-3 space-y-2"
          data-testid="schedule-stale-warning"
        >
          <p className="text-sm font-medium text-amber-100">Schedule needs attention</p>
          <p className="text-sm text-amber-100/90">{SCHEDULE_REPAIR_SETUP_LINK_COPY}</p>
          <ul className="list-disc list-inside text-sm text-amber-100/90 space-y-1">
            {attentionItems.map((item) => (
              <li key={item.key}>{item.description}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(league.active_season_label || league.active_stage_label) && (
        <dl
          className="grid gap-3 sm:grid-cols-2 rounded-lg border border-emerald-800/30 bg-emerald-950/10 px-4 py-3 text-sm"
          data-testid="schedule-active-season-stage"
        >
          <div>
            <dt className="text-secondary-500">Active season</dt>
            <dd className="text-emerald-100 font-medium">
              {displaySeasonLabel(league.active_season_label) || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-secondary-500">Active week</dt>
            <dd className="text-emerald-100 font-medium">{league.active_stage_label || '—'}</dd>
          </div>
        </dl>
      )}

      <ScheduleSyncStatusPanel
        leagueId={leagueId}
        compact
        onApplySuccess={onSetupRefresh}
      />

      <ScheduleSourcePanel
        leagueId={leagueId}
        leagueArchived={leagueArchived}
        onMutationSuccess={onSetupRefresh}
      />

      <NeonSportzUrlImportPanel leagueId={leagueId} onImportSuccess={onSetupRefresh} />

      <CsvImportPanel leagueId={leagueId} onImportSuccess={onSetupRefresh} />
    </div>
  );
};

export default ScheduleSetupStepPanel;
